// SS PWA — pages/AdminDiagnostics.tsx — 2026-06-03
//
// OPERATOR ADMIN VIEW. Cross-tenant. Shows raw rule names, error classes,
// counters, replay tools, synthetic tests. Gated to role=ss_operator.
//
// Route: /admin/diagnostics

import { useEffect, useState } from 'react';
import { TenantSwitcher } from '@/components/diagnostics/TenantSwitcher';
import { HealthBadges } from '@/components/diagnostics/HealthBadges';
import { AnomalyFeed } from '@/components/diagnostics/AnomalyFeed';
import { ReplayTimeline } from '@/components/diagnostics/ReplayTimeline';
import { SyntheticTestPanel } from '@/components/diagnostics/SyntheticTestPanel';
import {
  fetchAudit,
  fetchHealth,
  fetchReplayContact,
  fetchReplayCall,
  setDiagnosticsToken,
  getDiagnosticsToken,
  type AuditResponse,
  type HealthResponse,
  type ReplayResponse,
  DiagnosticsApiError,
} from '@/lib/api/diagnostics';
import { RoleGate, setRole } from '@/components/RoleGate';

function TokenBar() {
  const [token, setTokenState] = useState(getDiagnosticsToken());
  return (
    <div className="flex items-center gap-2">
      <input
        type="password"
        placeholder="DIAGNOSTICS_TOKEN"
        value={token}
        onChange={(e) => setTokenState(e.target.value)}
        className="w-64 rounded-md border border-ink-200 bg-white px-2 py-1 text-sm dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white"
      />
      <button
        type="button"
        onClick={() => {
          setDiagnosticsToken(token);
          // Soft reload of any in-flight queries by forcing the URL hash.
          window.dispatchEvent(new Event('ss-diag-token-changed'));
        }}
        className="rounded-md border border-ink-300 bg-white px-2 py-1 text-sm dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
      >
        Save
      </button>
    </div>
  );
}

function HoursPicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const options = [1, 6, 24, 72, 168];
  return (
    <select
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white"
    >
      {options.map((h) => (
        <option key={h} value={h}>
          {h < 24 ? `${h}h` : `${h / 24}d`}
        </option>
      ))}
    </select>
  );
}

function AdminDiagnosticsInner() {
  const [slug, setSlug] = useState('');
  const [hours, setHours] = useState(24);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [replay, setReplay] = useState<ReplayResponse | null>(null);
  const [replayId, setReplayId] = useState('');
  const [replayMode, setReplayMode] = useState<'contact' | 'call'>('contact');
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoadingHealth(true);
    void fetchHealth(slug)
      .then((h) => {
        if (!cancelled) setHealth(h);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof DiagnosticsApiError ? `API ${err.status}` : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingHealth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function runAudit() {
    if (!slug) return;
    setLoadingAudit(true);
    setError(null);
    try {
      const a = await fetchAudit(slug, { hours, depth: 'standard' });
      setAudit(a);
    } catch (err) {
      setError(err instanceof DiagnosticsApiError ? `API ${err.status}` : String(err));
    } finally {
      setLoadingAudit(false);
    }
  }

  async function runReplay() {
    if (!slug || !replayId) return;
    setError(null);
    try {
      const r = replayMode === 'contact'
        ? await fetchReplayContact(slug, replayId)
        : await fetchReplayCall(slug, replayId);
      setReplay(r);
    } catch (err) {
      setError(err instanceof DiagnosticsApiError ? `API ${err.status}` : String(err));
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-ink-900 dark:text-white">SS Diagnostics — Admin</h1>
        <span className="text-xs text-ink-500 dark:text-[#94A3B8]">operator view</span>
        <div className="ml-auto"><TokenBar /></div>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-ink-200 bg-white p-3 dark:border-[#1F2A37] dark:bg-[#0F172A]">
        <TenantSwitcher value={slug} onChange={setSlug} />
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-500 dark:text-[#94A3B8]">window</span>
          <HoursPicker value={hours} onChange={setHours} />
        </div>
        <button
          type="button"
          onClick={() => void runAudit()}
          disabled={!slug || loadingAudit}
          className="rounded-md border border-ink-300 bg-white px-3 py-1 text-sm text-ink-800 disabled:opacity-50 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
        >
          {loadingAudit ? 'Auditing…' : 'Run audit'}
        </button>
        {error ? <span className="text-xs text-red-500">{error}</span> : null}
      </div>

      <HealthBadges health={health} loading={loadingHealth} />

      <SyntheticTestPanel slug={slug} />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink-700 dark:text-[#94A3B8]">
          Anomalies in window
        </h2>
        <AnomalyFeed anomalies={audit?.raw_anomaly_list ?? []} />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-ink-700 dark:text-[#94A3B8]">
          Replay
        </h2>
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-2xl border border-ink-200 bg-white p-2 dark:border-[#1F2A37] dark:bg-[#0F172A]">
          <select
            value={replayMode}
            onChange={(e) => setReplayMode(e.target.value as 'contact' | 'call')}
            className="rounded-md border border-ink-200 bg-white px-2 py-1 text-sm dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white"
          >
            <option value="contact">Contact</option>
            <option value="call">Call</option>
          </select>
          <input
            type="text"
            placeholder={replayMode === 'contact' ? 'contactId' : 'callId'}
            value={replayId}
            onChange={(e) => setReplayId(e.target.value)}
            className="w-80 rounded-md border border-ink-200 bg-white px-2 py-1 text-sm dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-white"
          />
          <button
            type="button"
            onClick={() => void runReplay()}
            disabled={!slug || !replayId}
            className="rounded-md border border-ink-300 bg-white px-3 py-1 text-sm disabled:opacity-50 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white"
          >
            Run
          </button>
        </div>
        <ReplayTimeline replay={replay} />
      </section>

      {audit ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-ink-700 dark:text-[#94A3B8]">
            Per-call audits ({audit.calls.length})
          </h2>
          <ul className="divide-y divide-ink-100 rounded-2xl border border-ink-200 bg-white dark:divide-[#1F2A37] dark:border-[#1F2A37] dark:bg-[#0F172A]">
            {audit.calls.map((c) => (
              <li key={c.call_id} className="px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`size-2 rounded-full ${c.classification === 'ANOMALY' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                  <span className="font-mono text-ink-900 dark:text-white">{c.call_id}</span>
                  <span className="text-ink-500 dark:text-[#94A3B8]">{c.outcome}</span>
                  <span className="ml-auto text-ink-400">{c.duration_sec != null ? `${c.duration_sec}s` : ''}</span>
                </div>
                {c.anomalies.length > 0 ? (
                  <div className="mt-1 truncate text-amber-700 dark:text-amber-300">
                    {c.anomalies.map((a) => a.rule).join(' · ')}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="pt-4 text-xs text-ink-400">
        operator view · cross-tenant · raw rule names visible · do not share screenshots
      </footer>

      <div className="mt-8 rounded-2xl border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600 dark:border-[#1F2A37] dark:bg-[#1F2A37] dark:text-[#94A3B8]">
        <details>
          <summary className="cursor-pointer font-medium">Set role (one-time)</summary>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setRole('ss_operator')}
              className="rounded-md border border-ink-300 px-2 py-1 text-xs dark:border-[#334155]"
            >
              I am ss_operator
            </button>
            <button
              type="button"
              onClick={() => setRole('owner')}
              className="rounded-md border border-ink-300 px-2 py-1 text-xs dark:border-[#334155]"
            >
              I am owner
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}

export function AdminDiagnostics() {
  return (
    <RoleGate required="ss_operator">
      <AdminDiagnosticsInner />
    </RoleGate>
  );
}
